<template>
  <div
    class="regulatory-update-template"
    data-testid="regulatory-update-template"
  >
    <SpinnerLoader v-if="isLoading" :size="48" data-testid="loader" />

    <template v-else>
      <Notification :group="notificationGroup" />

      <RegulatoryUpdateCloseModal
        :is-open="isModalOpen"
        @toggle-modal-open="toggleModalOpen"
        @exit="exit"
      />

      <NavBar class="regulatory-update-template__navbar">
        <template #left>
          <ButtonIconContextual
            v-if="isFirstPage"
            label="fechar"
            icon="EF0030"
            data-testid="close-button"
            @click="close"
          />
          <ButtonIconContextual
            v-else
            label="voltar"
            icon="EA0030"
            @click="back"
          />
        </template>
      </NavBar>

      <HelperViewStatus v-if="hasError" generic-error />
    </template>

    <router-view
      class="regulatory-update-template__content"
      :show-content="showContent"
      @notify-error="notifyError"
      @set-is-loading="setIsLoading"
      @set-has-error="setHasError"
      @remove-confirm-close="removeConfirmClose"
      @go-to-next-page="goToNextPage"
      @go-to-previous-page="back"
      @change-tracker="changeTracker"
    />
  </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';
import {
  SpinnerLoader,
  NavBar,
  HelperViewStatus,
  ButtonIconContextual
} from '@warrenbrasil/nebraska-web';
import { Notification } from '@/modules/common/components/Notification';
import { NotificationTypes } from '@/modules/common/services/http/notification';
import {
  goToPreviousRoute,
  goToNextRoute
} from '@/modules/common/store/RegulatoryUpdateRoutesOrchestrator';
import { WarrenModules } from '@/types/navigation';
import RegulatoryUpdateCloseModal from '../../components/RegulatoryUpdateCloseModal/RegulatoryUpdateCloseModal.vue';
import { EventKnownProperties } from '@warrenbrasil/web-analytics';
import { FeatureFlags } from '@/types/models/FeatureFlags';
import { FeatureFlagsModule } from '@/modules/common/store/FeatureFlagsModule';

export enum RegulatoryUpdatePage {
  Announcement = 'regulatoryUpdateAnnouncement',
  PersonalData = 'regulatoryUpdatePersonalData',
  FiscalInfo = 'regulatoryUpdateFiscalInfo',
  Address = 'regulatoryUpdateAddress',
  AddressDocument = 'regulatoryUpdateAddressDocument',
  InvestmentProfile = 'regulatoryUpdateInvestmentProfile',
  CallOrchestrator = 'ochestrator'
}

@Component({
  components: {
    SpinnerLoader,
    RegulatoryUpdateCloseModal,
    NavBar,
    ButtonIconContextual,
    HelperViewStatus,
    Notification
  }
})
export default class RegulatoryUpdateTemplate extends Vue {
  public isLoading = true;

  public hasError = false;

  public shouldConfirmClose = true;

  public isModalOpen = false;

  public notificationGroup = 'wrn-notification-regulatory-update';

  private currentTracker: (remainingProps: EventKnownProperties) => void =
    () => {};

  public get showContent() {
    return !this.isLoading && !this.hasError;
  }

  public setIsLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  public setHasError(hasError: boolean) {
    this.hasError = hasError;
  }

  public removeConfirmClose() {
    this.shouldConfirmClose = false;
  }

  public toggleModalOpen() {
    this.isModalOpen = !this.isModalOpen;
  }

  public exit() {
    this.currentTracker({
      event_type: 'click',
      event_name: 'exit'
    });

    const redirectTo =
      (this.$route.query.cameFrom as WarrenModules) || WarrenModules.Home;
    this.$router.push({ name: redirectTo });
  }

  public close() {
    if (this.shouldConfirmClose) {
      this.toggleModalOpen();
      return;
    }

    this.exit();
  }

  public back() {
    this.currentTracker({
      event_type: 'click',
      event_name: 'back'
    });

    const previousPageIndex =
      this.regulatoryUpdateFlow.indexOf(
        this.$route.name as RegulatoryUpdatePage
      ) - 1;
    const previousPage = this.regulatoryUpdateFlow[previousPageIndex];

    if (previousPage === RegulatoryUpdatePage.CallOrchestrator) {
      goToPreviousRoute();
      return;
    }

    this.$router.back();
  }

  public goToNextPage() {
    const nextPageIndex =
      this.regulatoryUpdateFlow.indexOf(
        this.$route.name as RegulatoryUpdatePage
      ) + 1;

    let nextPage = this.regulatoryUpdateFlow[nextPageIndex];

    if (this.$route.name === RegulatoryUpdatePage.AddressDocument) {
      nextPage = RegulatoryUpdatePage.CallOrchestrator;
    }

    if (nextPage === RegulatoryUpdatePage.CallOrchestrator) {
      goToNextRoute();
      return;
    }

    this.$router.push({ name: nextPage });
  }

  public get isFirstPage() {
    return (
      this.regulatoryUpdateFlow.indexOf(
        this.$route.name as RegulatoryUpdatePage
      ) === 0
    );
  }

  public notifyError(message?: string) {
    this.$notify({
      group: this.notificationGroup,
      text:
        message ||
        'Houve um problema ao salvar seus dados. Aguarde alguns instantes e tente novamente.',
      type: NotificationTypes.error
    });
  }

  public changeTracker(
    newTracker: (remainingProps: EventKnownProperties) => void
  ) {
    this.currentTracker = newTracker;
  }

  public get regulatoryUpdateFlow() {
    const flow = [
      RegulatoryUpdatePage.Announcement,
      RegulatoryUpdatePage.CallOrchestrator,
      RegulatoryUpdatePage.PersonalData
    ];

    if (
      FeatureFlagsModule.isEnabled(FeatureFlags.REGULATORY_UPDATE_BUREAU) &&
      FeatureFlagsModule.isEnabled(
        FeatureFlags.ENABLE_FATCA_IN_REGULATORY_UPDATE
      )
    ) {
      flow.push(RegulatoryUpdatePage.FiscalInfo);
    }

    flow.push(
      RegulatoryUpdatePage.Address,
      RegulatoryUpdatePage.InvestmentProfile,
      RegulatoryUpdatePage.CallOrchestrator
    );

    return flow;
  }
}
</script>

<style lang="less" scoped>
.regulatory-update-template {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;

  &__navbar {
    position: absolute;
    top: 0;
    width: 100%;

    border: 0;
    padding: 0 var(--size-spacing-x350);

    @media @mobileDown {
      padding: 0;
    }
  }

  &__content {
    max-width: 672px;
    width: 100%;
    height: 100%;

    padding: @size-spacing-x400;
    padding-top: @size-spacing-x1800;

    @media @mobileDown {
      padding-top: @size-spacing-x1400;
    }
  }
}
</style>
